<template lang="pug">
    .chart-container.referrer-chart(v-loading="loading")
        h3.title {{ $t('Referrers') }}
        .error(v-if="error")
            Error(:message="$t(error.message)" :code="error.code")
        .chart-and-legend(v-else)
            .doughnut-wrap(v-if="chartData")
                DoughnutChart(ref="chart" :chart-data="chartData" :options="chartOptions" :plugins="[doughnutLabelsPlugin]")
            .no-data(v-else-if="!loading")
                i.el-icon-fa-globe.fas
                h4 {{ $t('Missing Data') }}
                p {{ $t('No referrer data available for this period') }}
            .legend(v-if="legendItems.length")
                ul.no-style-list
                    li(v-for="(item, index) in legendItems" :key="item.label")
                        el-button(
                            size="mini"
                            :class="{ disabled: disabledSegments.includes(index) }"
                            @click="toggleSegment(index, $event)"
                        )
                            span.color(:style="{ backgroundColor: item.color }")
                            span.label(:class="{ 'strike-through': disabledSegments.includes(index) }") {{ item.label }}
</template>

<i18n>
{
    "de": {
        "Referrers": "Referrer",
        "No referrer data available for this period": "Keine Referrer-Daten für diesen Zeitraum verfügbar",
        "Missing Data": "Es fehlen Daten",
        "visitors": "Besucher",
        "Others": "Sonstige"
    }
}
</i18n>

<script>
import StatisticsApi from '@/store/api/statistics'
import { useSetupStore } from '@/stores'

import getUnixTime from 'date-fns/getUnixTime'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'
import { appNow, appGetUnixTime } from '@/utils/timezone'

import DoughnutChart from '@/components/DoughnutChart'
import Error from '@/components/Error'
import referrerChartMixin from '@/views/cms/Dashboard/widgets/statistics/referrerChartMixin'

export default {
    name: 'ReferrerChartPartial',
    mixins: [referrerChartMixin],
    props: {
        domain: { type: String, default: null },
        daterange: { type: Array, default: () => [] },
        date: { type: Date, default: null },
        period: { type: String, default: 'day' },
    },
    data() {
        return {
            loading: false,
            error: false,
            referrerData: [],
        }
    },
    components: {
        DoughnutChart,
        Error,
    },
    watch: {
        domain() { this.refetch() },
        daterange() { this.refetch() },
        period() { this.refetch() },
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms
        },
        rawReferrerData() {
            return this.referrerData
        },
    },
    methods: {
        refetch() {
            if (!this.domain) return
            this.loading = true

            const from = this.daterange && this.daterange[0]
                ? getUnixTime(this.daterange[0])
                : appGetUnixTime(startOfDay(appNow()))
            const to = this.daterange && this.daterange[1]
                ? getUnixTime(this.daterange[1])
                : appGetUnixTime(endOfDay(appNow()))

            StatisticsApi.fetchReferrerStatistics({
                cms: this.activeCms.id,
                domain: this.domain,
                from: from,
                to: to,
            })
                .then(data => {
                    this.error = false
                    this.referrerData = data || []
                    this.disabledSegments = []
                    this.generate()
                    this.$nextTick(() => {
                        this.chartOptions.animation.duration = 0
                    })
                })
                .catch(err => {
                    this.chartData = null
                    this.legendItems = []
                    this.processedData = []
                    let code = 501
                    let message = 'An error occurred!'
                    if (err && err.graphQLErrors && err.graphQLErrors.length) {
                        const graphQLError = err.graphQLErrors[0]
                        if (graphQLError.message === 'Forbidden') code = 403
                        message = graphQLError.message
                    }
                    this.error = { code: code, message: message }
                })
                .finally(() => {
                    this.loading = false
                })
        },
    },
    mounted() {
        this.refetch()
    },
}
</script>

<style lang="sass" scoped>
.referrer-chart
    position: relative

.title
    margin: 0
    margin-bottom: 1rem
    font-weight: 300

.chart-and-legend
    display: flex
    min-height: 0
    gap: 0.5rem

.doughnut-wrap
    flex: 1
    min-height: 200px
    min-width: 0
    position: relative
    > div
        position: absolute
        inset: 0

.no-data
    flex-grow: 1
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    padding: 4rem
    text-align: center
    i
        font-size: 4rem
        color: rgba(0,0,0,0.2)
    h4
        margin-top: 1rem
        margin-bottom: 0
        font-size: 2rem
        font-weight: 300
        color: rgba(0,0,0,0.3)
    p
        color: rgba(0,0,0,0.3)

.error
    padding: 20px
    flex-grow: 1
    ::v-deep .title
        margin-top: 0

.legend
    min-height: 0
    overflow-y: auto
    max-width: 200px
    ul
        display: flex
        flex-direction: column
        li
            .el-button
                position: relative
                padding: 2px 8px 2px 22px
                border: none
                background: none !important
                white-space: nowrap
                overflow: hidden
                text-overflow: ellipsis
                max-width: 100%
                font-size: 12px
                line-height: 1.4
                &.disabled
                    opacity: 0.5
                span.color
                    display: inline-block
                    position: absolute
                    width: 12px
                    height: 12px
                    left: 4px
                    top: 50%
                    transform: translateY(-50%)
                    border-radius: 50%
                    outline: 1px solid #ffffff
                    box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.14), 0 1px 7px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -1px rgba(0, 0, 0, 0.2)
                span.label.strike-through
                    text-decoration: line-through
                    text-decoration-thickness: 2px
</style>
